import { useEffect, useMemo, useState } from 'react';

// The signed-in user's numeric id, as stored by authApi on login. Used as the
// default identity passed to a tab's visibility predicate (e.g. "is this record
// assigned to me").
function currentUserId() {
  return Number(localStorage.getItem('userId')) || 0;
}

/*
 * Generic detail-tab visibility gate.
 *
 * A module's tab entry (jobConfig.tabs, candidateConfig.tabs, …) may declare an
 * optional `visibleWhen(ctx)` predicate — a sync boolean or a Promise<boolean>.
 * A tab with no predicate is always shown, so this is purely additive and every
 * existing tab keeps rendering unchanged.
 *
 *   ctx = { id, moduleName, record, currentUserId }
 *
 * `record` is the detail record when the caller has it (the header flattens it
 * from form-groups); it may be undefined for callers that don't (a predicate
 * that needs more should fetch it itself, keyed by id). A gated tab stays HIDDEN
 * until its predicate resolves truthy, so a restricted tab never flashes on
 * screen before disappearing. On predicate error it fails OPEN (tab shown).
 */
export function useVisibleTabs(tabs, { id, moduleName, record } = {}) {
  const list = useMemo(() => (Array.isArray(tabs) ? tabs : []), [tabs]);
  const gated = useMemo(
    () => list.filter((tab) => typeof tab?.visibleWhen === 'function'),
    [list],
  );

  // "<id>:<tabKey>" → boolean once resolved. Keyed by id so a previous record's
  // result never leaks into another, and a key absent from the map means the
  // predicate for this record is still pending. Only the async callbacks write
  // to it (never synchronously inside the effect body).
  const [resolved, setResolved] = useState({});

  useEffect(() => {
    if (!gated.length) return undefined;
    let cancelled = false;
    const ctx = { id, moduleName, record, currentUserId: currentUserId() };
    gated.forEach((tab) => {
      const cacheKey = `${id}:${tab.key}`;
      Promise.resolve()
        .then(() => tab.visibleWhen(ctx))
        .then((visible) => {
          if (!cancelled) setResolved((prev) => ({ ...prev, [cacheKey]: visible !== false }));
        })
        .catch(() => {
          if (!cancelled) setResolved((prev) => ({ ...prev, [cacheKey]: true }));
        });
    });
    return () => { cancelled = true; };
  }, [gated, id, moduleName, record]);

  return useMemo(
    () => list.filter((tab) => (
      typeof tab?.visibleWhen !== 'function' ? true : resolved[`${id}:${tab.key}`] === true
    )),
    [list, resolved, id],
  );
}

export default useVisibleTabs;
